import { isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { View as RNView } from 'react-native';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { grid } from '@shared/styles';
import { useBoolean, useDebouncedState } from '@shared/util/hooks';
import classNames from './HoverTooltip.module.css';
import { HoverTooltipProps } from './HoverTooltipProps';
import { TOOLTIP_DELAY_MS } from './constants';
import { MBTextTooltip } from './text';
import { MBTooltipVerticalArrowAlignment, ViewRect } from './types';

export function HoverTooltip({ tooltip, children, ...otherProps }: HoverTooltipProps) {
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(false);
  const [showTooltip, setShowTooltip] = useDebouncedState(isHovered, 250);
  const [rect, setRect] = useState<ViewRect>();
  const ref = useRef<RNView>(null);

  useEffect(() => {
    setShowTooltip(isHovered);
  }, [isHovered, setShowTooltip]);

  useEffect(() => {
    if (isHovered) {
      const timer = setTimeout(() => {
        ref.current?.measure((_x, _y, width, height, pageX, pageY) => {
          setRect({ width, height, left: pageX, top: pageY });
        });
      }, TOOLTIP_DELAY_MS);
      return () => clearTimeout(timer);
    } else {
      setRect(undefined);
    }
  }, [isHovered, ref]);

  return (
    <MBHover onHoverStart={onHoverStart} onHoverEnd={onHoverEnd} {...otherProps}>
      <RNView ref={ref}>
        {isHovered && showTooltip && <TooltipAtRect tooltip={tooltip} rect={rect} />}
        {children}
      </RNView>
    </MBHover>
  );
}

export function TooltipAtRect({ tooltip, rect }: { tooltip?: string; rect?: ViewRect }) {
  if (isNil(rect) || isNil(tooltip)) {
    return null;
  }

  // anchor div positions itself at the top center of the hovered element
  const left = rect.left + rect.width / 2;
  let top = rect.top - grid(0.5);
  let verticalArrowAlignment: MBTooltipVerticalArrowAlignment = 'below';
  if (top < 50) {
    top = rect.top + rect.height + grid(6.5);
    verticalArrowAlignment = 'above';
  }

  // container div centers itself inside the above anchor, with its bottom
  // pinned to 0, so the tooltip grows upwaard from the anchor

  return ReactDOM.createPortal(
    <View className={classNames.tooltipAnchor} style={{ left, top }}>
      <View className={classNames.tooltipContainer}>
        <MBTextTooltip
          key={tooltip}
          text={tooltip}
          textSize="small"
          numberOfLines={3}
          verticalArrowAlignment={verticalArrowAlignment}
        />
      </View>
    </View>,
    document.body,
  );
}
